import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import ProviderContactForm from '../components/forms/ProviderContactForm';
import providerPageData from '../content/providers/index.json';
import { usePageMeta } from '../lib/usePageMeta';
import * as Icons from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Building2, ArrowRight, Check, ChevronDown, Rocket, Scale, Layers,
} from 'lucide-react';

const getIcon = (iconName) => {
  const IconComponent = Icons[iconName];
  return IconComponent ? <IconComponent /> : null;
};

const scrollToForm = () => {
  const el = document.querySelector('#pilot-form');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const ProvidersPage = () => {
  usePageMeta(
    'ResiAX for Landlords, Agents & Housing Providers — Provider Pilot',
    'Manage resident issues, communication and evidence in one place. Start with a few homes, residents or one team — no integration required. Register for the ResiAX provider pilot.'
  );

  React.useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const d = providerPageData;

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-[#1F1E1E] text-white relative overflow-hidden">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#B5541B]/10 rounded-full blur-3xl" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
              <Building2 className="w-5 h-5 text-[#D4722F]" />
              <span className="text-sm font-semibold text-[#D4722F] uppercase tracking-wide">{d.hero.eyebrow}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-testid="providers-hero-headline">
              {d.hero.headline}
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-4">
              {d.hero.body}
            </p>
            <p className="text-base font-semibold text-white/85 mb-10" data-testid="providers-hero-support-line">
              {d.hero.supportLine}
            </p>
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <Button
                onClick={scrollToForm}
                data-testid="providers-hero-apply-btn"
                className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-6 text-lg rounded-full shadow-lg group"
              >
                {d.hero.primaryCta}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => {
                  const el = document.querySelector('#capabilities');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="providers-hero-secondary-btn"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold h-auto px-8 py-6 text-lg rounded-full"
              >
                {d.hero.secondaryCta}
              </Button>
            </div>
            <Link
              to="/providers/enterprise"
              data-testid="providers-enterprise-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 hover:text-[#D4722F] transition-colors group"
            >
              {d.hero.enterpriseLink}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </section>

        {/* Who it is for */}
        <section className="py-16 bg-[#FFFAF8]" data-testid="providers-audience-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-3">{d.audience.heading}</h2>
            <p className="text-[#3B3535]/70 leading-relaxed max-w-2xl mx-auto mb-8">{d.audience.intro}</p>
            <div className="flex flex-wrap gap-2.5 justify-center">
              {(d.audience.labels || []).map((label, i) => (
                <span
                  key={i}
                  data-testid={`audience-chip-${i}`}
                  className="inline-flex items-center bg-white border border-[#B5541B]/20 text-[#3B3535] text-sm font-medium px-4 py-2 rounded-full hover:border-[#B5541B]/50 transition-colors"
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Problems */}
        <section className="py-20 bg-white" data-testid="providers-problems-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] text-center mb-12 max-w-3xl mx-auto">
              {d.problems.heading}
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              {(d.problems.items || []).map((p, i) => (
                <div key={i} className="bg-[#FFF8F5] rounded-2xl p-7 border border-[#B5541B]/10">
                  <h3 className="font-bold text-[#1F1E1E] mb-2">{p.title}</h3>
                  <p className="text-sm text-[#3B3535]/75 leading-relaxed">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Status quo cost */}
        {d.statusQuo && (
          <section className="py-20 bg-[#1F1E1E] text-white" data-testid="providers-statusquo-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 max-w-2xl mx-auto">
                <h2 className="text-3xl sm:text-4xl font-bold mb-3">{d.statusQuo.heading}</h2>
                <p className="text-white/60 leading-relaxed">{d.statusQuo.intro}</p>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {(d.statusQuo.items || []).map((item, i) => (
                  <div key={i} className="bg-white/5 rounded-2xl p-7 border border-white/10" data-testid={`statusquo-card-${i}`}>
                    <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                    <p className="text-sm text-white/55 leading-relaxed mb-4">{item.pain}</p>
                    <div className="flex items-start gap-2.5 bg-[#B5541B]/15 border border-[#B5541B]/30 rounded-xl p-4">
                      <Check className="w-4 h-4 text-[#D4722F] mt-0.5 shrink-0" />
                      <p className="text-sm text-white/85 leading-relaxed">{item.withResiax}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* What you can do */}
        <section id="capabilities" className="py-20 bg-[#FFFAF8] scroll-mt-20" data-testid="providers-capabilities-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-3">{d.capabilities.heading}</h2>
              <p className="text-[#3B3535]/70 leading-relaxed">{d.capabilities.intro}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(d.capabilities.items || []).map((c, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:border-[#B5541B]/30 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#B5541B]/10 flex items-center justify-center text-[#B5541B] mb-4">
                    <span className="w-6 h-6">{getIcon(c.icon)}</span>
                  </div>
                  <h3 className="font-bold text-[#1F1E1E] mb-2">{c.title}</h3>
                  <p className="text-sm text-[#3B3535]/70 leading-relaxed">{c.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Resident-controlled connection */}
        <section className="py-20 bg-[#1F1E1E] text-white" data-testid="providers-connection-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">{d.residentConnection.heading}</h2>
              <p className="text-white/60">{d.residentConnection.intro}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {(d.residentConnection.roles || []).map((r, i) => (
                <div key={i} className="bg-white/5 rounded-2xl p-7 border border-white/10">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#B5541B] to-[#8B3F14] flex items-center justify-center mb-4">
                    <span className="w-6 h-6 text-white">{getIcon(r.icon)}</span>
                  </div>
                  <h3 className="text-lg font-bold mb-2">{r.title}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{r.description}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-white/60 max-w-xl mx-auto" data-testid="connection-support-line">
              {d.residentConnection.supportLine}
            </p>
          </div>
        </section>

        {/* Provider pilot */}
        <section id="pilot" className="py-20 bg-white scroll-mt-20" data-testid="providers-pilot-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#B5541B]/10 px-4 py-2 rounded-full mb-6">
                <Rocket className="w-5 h-5 text-[#B5541B]" />
                <span className="text-sm font-semibold text-[#B5541B] uppercase tracking-wide">{d.providerPilot.badge}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-4" data-testid="provider-pilot-heading">{d.providerPilot.heading}</h2>
              <p className="text-[#3B3535]/70 max-w-2xl mx-auto leading-relaxed">{d.providerPilot.body}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {(d.providerPilot.includes || []).map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-[#FFF8F5] rounded-xl p-4 border border-[#B5541B]/10 text-sm text-[#3B3535]">
                  <Check className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />
                  {item}
                </div>
              ))}
            </div>
            <p className="text-sm text-[#3B3535]/70 leading-relaxed bg-[#FFFAF8] border border-[#B5541B]/15 rounded-xl p-5 max-w-3xl mx-auto mb-8" data-testid="pilot-no-price-line">
              {d.providerPilot.noPriceLine}
            </p>
            <div className="text-center">
              <Button
                onClick={scrollToForm}
                data-testid="pilot-apply-cta"
                className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-6 text-lg rounded-full shadow-lg group"
              >
                {d.providerPilot.ctaLabel}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </section>

        {/* Pilot walkthrough */}
        {d.pilotWalkthrough && (
          <section className="py-20 bg-[#FFF8F5]" data-testid="providers-walkthrough-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 max-w-2xl mx-auto">
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-3">{d.pilotWalkthrough.heading}</h2>
                <p className="text-[#3B3535]/70 leading-relaxed">{d.pilotWalkthrough.intro}</p>
              </div>
              <div className="space-y-0">
                {(d.pilotWalkthrough.steps || []).map((s, i, arr) => (
                  <div key={i} className="flex gap-5" data-testid={`walkthrough-step-${i}`}>
                    <div className="flex flex-col items-center">
                      <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#B5541B] to-[#8B3F14] text-white flex items-center justify-center text-sm font-bold shrink-0">
                        {i + 1}
                      </span>
                      {i < arr.length - 1 && <span className="w-0.5 flex-1 bg-[#B5541B]/20 my-1" />}
                    </div>
                    <div className={i < arr.length - 1 ? 'pb-8' : ''}>
                      <p className="text-xs font-bold uppercase tracking-widest text-[#B5541B] mb-1">{s.phase}</p>
                      <h3 className="font-bold text-[#1F1E1E] mb-1.5">{s.title}</h3>
                      <p className="text-sm text-[#3B3535]/75 leading-relaxed">{s.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* After the pilot */}
        <section className="py-20 bg-[#FFFAF8]" data-testid="providers-after-pilot-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 max-w-3xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-4">{d.afterPilot.heading}</h2>
              <p className="text-[#3B3535]/70 leading-relaxed">{d.afterPilot.body}</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
              <p className="text-sm font-semibold text-[#B5541B] uppercase tracking-wide mb-5">What the review looks at</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(d.afterPilot.reviewAreas || []).map((a, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-[#3B3535]">
                    <Check className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />
                    {a}
                  </div>
                ))}
              </div>
              <p className="text-sm text-[#3B3535]/70 leading-relaxed mt-6 pt-5 border-t border-gray-100">{d.afterPilot.closing}</p>
            </div>
          </div>
        </section>

        {/* Bridges: existing systems + compliance */}
        <section className="py-16 bg-white" data-testid="providers-bridges-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
            <div className="bg-[#1F1E1E] text-white rounded-2xl p-8 flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                <Layers className="w-6 h-6 text-[#D4722F]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{d.systemsBridge.heading}</h3>
              <p className="text-sm text-white/70 leading-relaxed mb-5">{d.systemsBridge.body}</p>
              {d.systemsBridge.handoffExamples && (
                <div className="mb-6" data-testid="systems-handoff-examples">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#D4722F] mb-3">{d.systemsBridge.handoffHeading}</p>
                  <ul className="space-y-2">
                    {d.systemsBridge.handoffExamples.map((ex, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/70 leading-relaxed">
                        <Check className="w-4 h-4 text-[#D4722F] mt-0.5 shrink-0" />
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Link
                to="/providers/enterprise"
                data-testid="systems-bridge-enterprise-link"
                className="self-start inline-flex items-center gap-1.5 bg-[#B5541B] hover:bg-[#8B3F14] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all group"
              >
                {d.systemsBridge.ctaLabel}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
            <div className="bg-[#FFF8F5] rounded-2xl p-8 border border-[#B5541B]/10 flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-[#B5541B]/10 flex items-center justify-center mb-4">
                <Scale className="w-6 h-6 text-[#B5541B]" />
              </div>
              <h3 className="text-xl font-bold text-[#1F1E1E] mb-3">{d.complianceBridge.heading}</h3>
              <p className="text-sm text-[#3B3535]/75 leading-relaxed mb-6 flex-1">{d.complianceBridge.body}</p>
              <Link
                to="/compliance"
                data-testid="compliance-bridge-link"
                className="self-start inline-flex items-center gap-1.5 bg-[#1F1E1E] hover:bg-[#2D2A2A] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all group"
              >
                {d.complianceBridge.ctaLabel}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-[#FFFAF8]" data-testid="providers-faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] text-center mb-10">{d.faq.heading}</h2>
            <div className="space-y-3">
              {(d.faq.items || []).map((f, i) => (
                <details key={i} className="group bg-white rounded-xl border border-gray-100 shadow-sm" data-testid={`provider-faq-${i}`}>
                  <summary className="flex items-center justify-between gap-4 cursor-pointer list-none p-5 font-semibold text-[#1F1E1E]">
                    {f.question}
                    <ChevronDown className="w-5 h-5 text-[#B5541B] shrink-0 group-open:rotate-180 transition-transform" />
                  </summary>
                  <p className="px-5 pb-5 text-sm text-[#3B3535]/75 leading-relaxed">{f.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Pilot form */}
        <section id="pilot-form" className="py-20 bg-white scroll-mt-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-3">{d.form.heading}</h2>
              <p className="text-[#3B3535]/70">{d.form.intro}</p>
            </div>
            <ProviderContactForm />
            <p className="text-center text-sm text-[#3B3535]/60 mt-6">
              Or email us directly:{' '}
              <a href={`mailto:${d.contact.email}`} className="text-[#B5541B] hover:text-[#8B3F14] underline">
                {d.contact.email}
              </a>
              {' '}&bull;{' '}
              <a href={d.contact.website} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#1F1E1E] hover:text-[#B5541B] transition-colors">
                {d.contact.company}
              </a>
            </p>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default ProvidersPage;
